/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Texture, Polygon, Point } from 'pixi.js-legacy';
import { SpriteTicket } from '@appPlanApplication/tickets/SpriteTicket';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import {
	MILESTONE_SIZE_OFFSET,
	MILESTONE_SIZE_OFFSET2,
} from '../../canvas-common/interfaces/milestone-size.interface';
import { Side } from '@touchplan/common';

import { IMAGE_PADDING } from '../../canvas-common/interfaces/image-padding.interface';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { ticketSchema } from '@appPlanApplication/ticket-components/TicketSchema';

export interface ISizeOffset {
	width: number;
	height: number;
}

const pullSizeOffset = MILESTONE_SIZE_OFFSET2.PULL;
const activeSizeOffset = MILESTONE_SIZE_OFFSET2.ACTIVE;
/**
 *This class is the main representation of a ticket. It has a simple component api to add individual pieces associated with the same data
 */
export class SpriteMilestone extends SpriteTicket {
	public offsetSize: ISizeOffset = {
		width: MILESTONE_SIZE_OFFSET.WIDTH,
		height: MILESTONE_SIZE_OFFSET.HEIGHT,
	};
	public override type: string = TicketType.Milestone;
	/**
	 * @param {PIXI.Texture} ticketShape
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {TicketContainer} ticketContainer
	 */
	constructor(ticketShape: Texture, canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super(ticketShape, canvasAngularLand, ticketContainer);

		this.x = -activeSizeOffset / 2 - IMAGE_PADDING.ACTIVE_SHAPE;
		this.y = -activeSizeOffset / 2 - IMAGE_PADDING.ACTIVE_SHAPE;

		const data = ticketContainer.getData();

		// Add the milstone schema to this ticket
		if (data.isGhost) {
			ticketContainer.components.addDynamicComponents(
				ticketSchema.createGhostMilestoneSchema(canvasAngularLand, ticketContainer)
			);
		} else {
			ticketContainer.components.addDynamicComponents(
				ticketSchema.createMilestoneSchema(canvasAngularLand, ticketContainer)
			);
		}
	}
	/**
	 * Called once the data container data changes
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {TicketContainer} ticketContainer
	 * @returns {any}
	 */
	public override updateData(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super.updateData(canvasAngularLand, ticketContainer);
		const data = this.getData();
		const layout = ticketContainer.getLayout();
		let points;
		if (data.view.side === Side.PULL) {
			this.offsetSize.width = layout.width + pullSizeOffset;
			this.offsetSize.height = layout.height + pullSizeOffset;

			this.x = -pullSizeOffset / 2 - IMAGE_PADDING.PULL_SHAPE;
			this.y = -pullSizeOffset / 2 - IMAGE_PADDING.PULL_SHAPE;
			this.width = this.offsetSize.width + IMAGE_PADDING.PULL_SHAPE * 2;
			this.height = this.offsetSize.height + IMAGE_PADDING.PULL_SHAPE * 2;

			points = [
				new Point(-pullSizeOffset / 2, -pullSizeOffset / 2 + this.offsetSize.height / 2), // left point
				new Point(-pullSizeOffset / 2 + this.offsetSize.width / 2, -pullSizeOffset / 2), // top point
				new Point(
					-pullSizeOffset / 2 + this.offsetSize.width,
					-pullSizeOffset / 2 + this.offsetSize.height / 2
				), // right point
				new Point(
					-pullSizeOffset / 2 + this.offsetSize.width / 2,
					-pullSizeOffset / 2 + this.offsetSize.height
				), // bottom point
			];
		} else {
			this.offsetSize.width = layout.width + activeSizeOffset;
			this.offsetSize.height = layout.height + activeSizeOffset;

			this.x = -activeSizeOffset / 2 - IMAGE_PADDING.ACTIVE_SHAPE;
			this.y = -activeSizeOffset / 2 - IMAGE_PADDING.ACTIVE_SHAPE;
			this.width = this.offsetSize.width + IMAGE_PADDING.ACTIVE_SHAPE * 2;
			this.height = this.offsetSize.height + IMAGE_PADDING.ACTIVE_SHAPE * 2;

			points = [
				new Point(-activeSizeOffset / 2, -activeSizeOffset / 2 + this.offsetSize.height / 2), // left point
				new Point(-activeSizeOffset / 2 + this.offsetSize.width / 2, -activeSizeOffset / 2), // top point
				new Point(
					-activeSizeOffset / 2 + this.offsetSize.width,
					-activeSizeOffset / 2 + this.offsetSize.height / 2
				), // right point
				new Point(
					-activeSizeOffset / 2 + this.offsetSize.width / 2,
					-activeSizeOffset / 2 + this.offsetSize.height
				), // bottom point
			];
		}

		ticketContainer.SURFACE.hitArea = new Polygon(points);

		return data;
	}
}
